'use client';

import { useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import useScrollReveal from '@/hooks/useScrollReveal';

interface CaseStudy {
  id: string;
  title: string;
  category: 'justice-impacted' | 'nonprofit' | 'service-business' | 'automation';
  challenge: string;
  whatWeBuilt: string;
  structuralChange: string;
  outcome: string;
  image: string;
  imageAlt: string;
  accentColor: string;
}

interface CategoryItem {
  id: string;
  label: string;
  icon: string;
  color: string;
}

// ─── Sub-components with hooks at top level ───────────────────────────────────

function CaseStudyCard({
  study,
  index,
  categories




}: {study: CaseStudy;index: number;categories: CategoryItem[];}) {
  const { ref: studyRef, isVisible: studyVisible } = useScrollReveal<HTMLDivElement>();
  return (
    <div
      key={study.id}
      ref={studyRef}
      className={`group grid grid-cols-1 lg:grid-cols-2 gap-0 overflow-hidden shadow-xl transition-all duration-1000 hover:shadow-2xl ${
      index % 2 === 0 ? '' : 'lg:grid-flow-dense'} ${
      studyVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
      style={{ transitionDelay: `${Math.min(index, 3) * 100}ms` }}>

      {/* Image Column */}
      <div
        className={`relative h-[350px] sm:h-[400px] md:h-[450px] lg:h-[600px] overflow-hidden ${
        index % 2 === 0 ? '' : 'lg:col-start-2'}`
        }>

        <AppImage
          src={study.image}
          alt={study.imageAlt}
          width={800}
          height={600}
          className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700" />

        <div
          className={`absolute top-4 sm:top-6 left-4 sm:left-6 z-20 inline-flex items-center gap-2 sm:gap-3 px-3 py-1.5 sm:px-4 sm:py-2 bg-${study.accentColor} text-white text-[10px] sm:text-xs font-body font-bold uppercase tracking-wider shadow-xl transition-all duration-300 group-hover:scale-105`}>

          <Icon
            name={categories.find((c) => c.id === study.category)?.icon || 'SparklesIcon'}
            size={14}
            className="sm:w-[18px] sm:h-[18px]" />

          <span className="hidden sm:inline">
            {categories.find((c) => c.id === study.category)?.label}
          </span>
          <span className="sm:hidden">
            {categories.find((c) => c.id === study.category)?.label.split(' ')[0]}
          </span>
        </div>
        {/* Accent bar */}
        <div
          className={`absolute top-0 left-0 w-1 h-full bg-${study.accentColor} group-hover:w-2 transition-all duration-300`}>
        </div>
      </div>

      {/* Content Column */}
      <div
        className={`bg-brand-offwhite p-6 sm:p-8 md:p-10 lg:p-12 flex flex-col justify-center ${
        index % 2 === 0 ? '' : 'lg:col-start-1 lg:row-start-1'}`
        }>

        <h3
          className={`font-headline text-2xl sm:text-3xl md:text-4xl text-brand-plum mb-6 sm:mb-8 font-bold border-l-4 border-${study.accentColor} pl-4 sm:pl-6 leading-tight`}>

          {study.title}
        </h3>

        <div className="space-y-4 sm:space-y-6 pl-4 sm:pl-6">
          <div>
            <h4
              className={`font-body font-bold text-xs sm:text-sm text-${study.accentColor} mb-2 uppercase tracking-wider`}>

              The Challenge
            </h4>
            <p className="font-body text-xs sm:text-sm text-text-secondary leading-relaxed">
              {study.challenge}
            </p>
          </div>

          <div>
            <h4
              className={`font-body font-bold text-xs sm:text-sm text-${study.accentColor} mb-2 uppercase tracking-wider`}>

              What We Built
            </h4>
            <p className="font-body text-xs sm:text-sm text-text-secondary leading-relaxed">
              {study.whatWeBuilt}
            </p>
          </div>

          <div>
            <h4
              className={`font-body font-bold text-xs sm:text-sm text-${study.accentColor} mb-2 uppercase tracking-wider`}>

              The Structural Change
            </h4>
            <p className="font-body text-xs sm:text-sm text-text-secondary leading-relaxed">
              {study.structuralChange}
            </p>
          </div>

          <div className="pt-3 sm:pt-4 border-t-2 border-brand-plum">
            <h4 className="font-body font-bold text-xs sm:text-sm text-brand-plum mb-2 uppercase tracking-wider">
              Outcome
            </h4>
            <p className="font-body text-sm sm:text-base text-brand-plum font-bold leading-relaxed">
              {study.outcome}
            </p>
          </div>
        </div>
      </div>
    </div>);

}

function GridHighlightCard({
  study,
  index,
  categories




}: {study: CaseStudy;index: number;categories: CategoryItem[];}) {
  const { ref: gridRef, isVisible: gridVisible } = useScrollReveal<HTMLDivElement>();
  return (
    <div
      ref={gridRef}
      className={`group relative h-[450px] overflow-hidden shadow-2xl transition-all duration-700 ${
      gridVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`
      }
      style={{ transitionDelay: `${index * 100}ms` }}>

      <AppImage
        src={study.image}
        alt={study.imageAlt}
        width={600}
        height={600}
        className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700" />

      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent group-hover:from-black/70 transition-all duration-500"></div>
      <div className="absolute bottom-0 left-0 right-0 p-8">
        <div
          className={`inline-block px-4 py-2 bg-${study.accentColor} text-white text-sm font-body font-bold mb-4 uppercase tracking-wider`}>

          {categories.find((c) => c.id === study.category)?.label}
        </div>
        <h4 className="font-headline text-3xl text-white font-bold leading-tight drop-shadow-lg">
          {study.title}
        </h4>
      </div>
    </div>);

}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function PortfolioGrid() {
  const [activeFilter, setActiveFilter] = useState<string>('all');
  const { ref: filterRef, isVisible: filterVisible } = useScrollReveal<HTMLDivElement>();

  const caseStudies: CaseStudy[] = [
  {
    id: '1',
    title: 'Texas Tree Transformations',
    category: 'service-business',
    challenge:
    'Growing tree service company needed a full digital infrastructure partner to compete in the Dallas market, establish professional brand presence, and build systems that could scale with the business.',
    whatWeBuilt:
    'Full digital infrastructure partnership: brand positioning, website development, SEO strategy, automation systems, CRM integration, and emergency response marketing strategy. We lead all email marketing, creative direction, design assets, drone-based content production, and full social media content creation and management.',
    structuralChange:
    'Transformed from a basic service provider into a full-scale digital operation with a professional brand identity, automated lead generation, drone-based content, and a complete social media presence managed end-to-end.',
    outcome:
    'Established as a premium tree service provider with automated marketing systems, drone-based content driving engagement, CRM-integrated operations, and consistent multi-channel client communication.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_13fd18271-1772170556682.png",
    imageAlt: 'Professional tree service team working on large tree with specialized equipment',
    accentColor: 'brand-green'
  },
  {
    id: '2',
    title: 'Prison Entrepreneurship Program (PEP)',
    category: 'nonprofit',
    challenge:
    'Transformative nonprofit needed modern digital infrastructure to scale volunteer recruitment, manage complex donor relationships, streamline educational program delivery, and maximize their Google Ad Grant investment.',
    whatWeBuilt:
    'Website rebuild, SEO management, Google Ad Grant management, volunteer onboarding system architecture, LMS implementation, donation infrastructure, and Salesforce integration strategy. We provide ongoing digital strategy consulting, full email marketing management, creative/design services, and complete social media content creation and management.',
    structuralChange:
    'Moved from fragmented manual processes to a unified digital ecosystem — with a rebuilt website, automated volunteer onboarding, a functioning LMS, and Salesforce-integrated donor management — enabling scalable, data-driven growth.',
    outcome:
    'Dramatically improved volunteer recruitment efficiency, enhanced donor engagement through automated systems, maximized Google Ad Grant ROI, and established robust infrastructure for continued program expansion.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1a5e7d935-1777416627165.png",
    imageAlt: 'Educational program participants engaged in entrepreneurship training session',
    accentColor: 'brand-orange'
  },
  {
    id: '3',
    title: 'Entre Capital',
    category: 'service-business',
    challenge:
    'Financial services firm needed professional brand communications and marketing materials to effectively present loan products, attract investors, and align their digital fundraising strategy.',
    whatWeBuilt:
    'Brand and communications strategy including loan product one-sheets, sponsorship materials, borrower-facing content, and digital fundraising alignment. We provide ongoing email marketing management and content creation support.',
    structuralChange:
    'Elevated brand presentation from basic materials to polished financial services communications — with borrower-facing content and investor-ready collateral that build credibility across every touchpoint.',
    outcome:
    'Professional brand presence with refined marketing collateral enabling effective investor relations, borrower acquisition, and ongoing content support that keeps communications consistent and compelling.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1fe785e5c-1784010832247.png",
    imageAlt: 'Professional financial services presentation materials and brand collateral',
    accentColor: 'brand-plum'
  },
  {
    id: '4',
    title: 'RPM Diesel Services',
    category: 'automation',
    challenge:
    'Diesel service company drowning in manual service request intake, spending excessive time on paperwork and disconnected systems instead of actual service delivery.',
    whatWeBuilt:
    'Custom Progressive Web App build for service request intake, workflow automation, Gravity Forms architecture, QuickBooks estimate integration, SMS/email notification systems, and operational data mapping. We provide ongoing email marketing and creative support.',
    structuralChange:
    'Eliminated manual intake processes entirely — replaced with a custom PWA that routes service requests through automated workflows, generates QuickBooks estimates, and triggers SMS/email notifications without manual intervention.',
    outcome:
    'Massive reduction in administrative overhead, faster customer response times, improved client experience, and direct integration with accounting systems — all supported by ongoing marketing and creative services.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_129192826-1777416625002.png",
    imageAlt: 'Mobile service request app interface showing automated workflow system',
    accentColor: 'brand-teal'
  },
  {
    id: '5',
    title: 'Maple & Bluebonnet',
    category: 'service-business',
    challenge:
    'Artisan food brand needed cohesive marketing strategy for pop-up events and seasonal product launches to build consistent brand recognition and grow a loyal customer base.',
    whatWeBuilt:
    'Brand positioning, pop-up marketing strategy, event-based promotions, seasonal storytelling, and visual identity direction. We manage email marketing, creative/design services, and full social media content creation and management.',
    structuralChange:
    'Transformed from ad-hoc event marketing to strategic brand storytelling — with a defined visual identity, planned seasonal campaigns, and a fully managed social media presence that builds community between events.',
    outcome:
    'Established recognizable brand presence, increased event attendance, and created a repeatable marketing framework for seasonal launches backed by consistent email and social media management.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b9040def-1777416626750.png",
    imageAlt: 'Artisan food products displayed at pop-up market event with branded materials',
    accentColor: 'brand-orange'
  },
  {
    id: '6',
    title: 'Vitality Tree Care',
    category: 'service-business',
    challenge:
    'Tree care service needed a professional online presence and search visibility to compete with established competitors and convert local search traffic into booked jobs.',
    whatWeBuilt:
    'Website development, SEO optimization strategy, service positioning refinement, and content architecture. Ongoing email marketing and creative support.',
    structuralChange:
    'Moved from minimal online presence to a professionally built website optimized for local search — with service positioning and content architecture designed to convert visitors into customers.',
    outcome:
    'Improved search rankings, increased organic traffic, and a professional digital presence that drives lead generation — supported by ongoing email marketing and creative services.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1a6422721-1771799124288.png",
    imageAlt: 'Professional arborist team providing expert tree care services',
    accentColor: 'brand-green'
  },
  {
    id: '7',
    title: 'Good Riddance Cleaning & Sanitation Services',
    category: 'service-business',
    challenge:
    'Specialized cleaning service needed to establish authority in certified kitchen exhaust hood cleaning and capture high-value commercial clients searching for NFPA Code 96 compliance work.',
    whatWeBuilt:
    'SEO-driven content strategy focused on certified kitchen exhaust hood cleaning per NFPA Code 96, website optimization, and technical positioning. Ongoing email marketing and creative services.',
    structuralChange:
    'Repositioned from a generic cleaning service to a recognized technical authority in compliance-driven commercial kitchen exhaust cleaning — with content and SEO strategy built around NFPA Code 96 standards.',
    outcome:
    'Improved search visibility for high-value commercial cleaning keywords, established credibility in a specialized compliance-driven niche, and ongoing marketing support to sustain growth.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_145bd7fd7-1779630052774.png",
    imageAlt: 'Commercial kitchen exhaust system cleaning and NFPA compliance work',
    accentColor: 'brand-teal'
  },
  {
    id: '8',
    title: 'Meraki Builder',
    category: 'service-business',
    challenge:
    'Construction company needed consistent content strategy to improve search visibility, establish thought leadership, and generate organic leads in a competitive market.',
    whatWeBuilt:
    'SEO content strategy, blog architecture, and ongoing search visibility growth. We manage email marketing, creative/design services, and full social media content creation and management.',
    structuralChange:
    'Created a systematic content production framework — with a structured blog, SEO strategy, and fully managed social media — that positions the company as an industry expert while driving organic search traffic.',
    outcome:
    'Established consistent content presence, improved search rankings, and built a foundation for long-term organic growth supported by full social media and email marketing management.',
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1b45b9061-1768712546438.png',
    imageAlt: 'Construction project planning and professional building services',
    accentColor: 'brand-plum'
  },
  {
    id: '9',
    title: 'Cream Coffee Shop',
    category: 'service-business',
    challenge:
    'Local coffee shop needed a content strategy to increase local search presence, tell their brand story beyond social media, and build an owned digital platform.',
    whatWeBuilt:
    'Structured blog content strategy, SEO content production, brand storytelling, and digital presence refinement. We lead email marketing, creative direction, and full social media content creation and management.',
    structuralChange:
    'Moved from social-media-only presence to a multi-channel content strategy — with a search-optimized blog, consistent brand storytelling, and fully managed email and social channels driving discovery and loyalty.',
    outcome:
    'Increased local search visibility, enhanced brand storytelling, and created an owned content platform independent of social algorithms — backed by ongoing creative direction and social media management.',
    image: 'https://images.unsplash.com/photo-1728330933849-8f99fe9a20a8',
    imageAlt: 'Cozy coffee shop interior with artisan coffee and community atmosphere',
    accentColor: 'brand-orange'
  },
  {
    id: '10',
    title: "Trucker's Sidekick",
    category: 'service-business',
    challenge:
    'Virtual assistant service for truck drivers needed professional digital presence and clear positioning to attract clients in an underserved niche market with no established competitors.',
    whatWeBuilt:
    'Website and brand positioning strategy for a virtual assistant service supporting truck drivers, including marketing infrastructure planning and creative direction. Ongoing email marketing and creative services.',
    structuralChange:
    'Established a professional service brand in a niche market — with industry-specific positioning, a clear value proposition, and marketing infrastructure built to attract and convert truck driver clients.',
    outcome:
    'Professional market entry with targeted positioning that resonates with the truck driver audience, differentiates from generic VA services, and ongoing marketing support to sustain momentum.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_199693987-1784915381166.png",
    imageAlt: 'Truck driver using mobile technology and virtual assistant services',
    accentColor: 'brand-teal'
  },
  {
    id: '11',
    title: 'Texas Tree Scout App',
    category: 'automation',
    challenge:
    'Internal need for a field logging system to document hazardous trees with GPS coordinates and structured reporting for tree service operations — replacing unreliable paper-based processes.',
    whatWeBuilt:
    'Progressive Web App development using Gravity Forms and Super PWA for field logging of hazardous trees, including GPS capture, structured reporting workflows, and operational automation systems.',
    structuralChange:
    'Replaced paper-based field notes with a mobile-first PWA featuring automatic GPS tagging, photo documentation, and centralized structured reporting — built entirely on Gravity Forms and Super PWA infrastructure.',
    outcome:
    'Streamlined field operations, improved data accuracy, GPS-tagged hazard documentation, and centralized reporting that supports operational planning and risk management.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1db3a86af-1784915380066.png",
    imageAlt: 'Field worker using mobile app for tree inspection and GPS logging',
    accentColor: 'brand-green'
  },
  {
    id: '12',
    title: 'LeadFire Pro',
    category: 'automation',
    challenge:
    'Service businesses need systematic B2B lead generation and direct-mail automation but lack tools designed for their specific outbound targeting needs and budget realities.',
    whatWeBuilt:
    'AI-driven B2B lead generation and direct-mail automation platform for service industries. Subscription-based growth system focused on structured outbound targeting and data-driven opportunity identification. Currently in development.',
    structuralChange:
    'Building an automated lead generation system that combines AI-driven targeting with direct-mail execution — giving service businesses enterprise-level outbound capabilities through an accessible subscription model.',
    outcome:
    'In development — designed to provide service businesses with structured outbound targeting, data-driven opportunity identification, and scalable lead generation through a subscription-based platform.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_177b5b777-1774209989370.png",
    imageAlt: 'AI-powered lead generation dashboard and automation platform interface',
    accentColor: 'brand-plum'
  }];


  const categories: CategoryItem[] = [
  { id: 'all', label: 'All Work', icon: 'SparklesIcon', color: 'brand-teal' },
  { id: 'justice-impacted', label: 'Justice-Impacted & Second-Chance', icon: 'HandRaisedIcon', color: 'brand-green' },
  { id: 'nonprofit', label: 'Nonprofit Infrastructure', icon: 'HeartIcon', color: 'brand-orange' },
  { id: 'service-business', label: 'Service & Field-Based', icon: 'WrenchScrewdriverIcon', color: 'brand-orange' },
  { id: 'automation', label: 'Automation & Workflow', icon: 'CogIcon', color: 'brand-plum' }];


  const filteredStudies =
  activeFilter === 'all' ? caseStudies : caseStudies.filter((study) => study.category === activeFilter);

  return (
    <section className="py-12 sm:py-16 md:py-20 lg:py-28 bg-white">
      <div ref={filterRef} className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 lg:px-12">
        {/* Filter Bar */}
        <div
          className={`mb-10 sm:mb-12 md:mb-16 transition-all duration-1000 ${
          filterVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`
          }>

          <div className="flex flex-wrap gap-2 sm:gap-3 md:gap-4">
            {categories.map((category) =>
            <button
              key={category.id}
              onClick={() => setActiveFilter(category.id)}
              className={`group flex items-center gap-2 sm:gap-3 px-3 py-2 sm:px-4 md:px-6 sm:py-2.5 md:py-3 font-body font-bold text-xs sm:text-sm transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl min-h-[44px] ${
              activeFilter === category.id ?
              `bg-${category.color} text-white` :
              'bg-brand-offwhite text-brand-plum hover:bg-gray-200'}`
              }>

                <Icon name={category.icon} size={16} className="sm:w-5 sm:h-5" />
                <span className="hidden sm:inline">{category.label}</span>
                <span className="sm:hidden">{category.label.split(' ')[0]}</span>
              </button>
            )}
          </div>
        </div>

        {/* Case Studies Grid */}
        <div className="space-y-12 sm:space-y-16 md:space-y-20">
          {filteredStudies.map((study, index) =>
          <CaseStudyCard key={study.id} study={study} index={index} categories={categories} />
          )}
        </div>

        {/* Photo Grid Section - Additional Project Highlights */}
        {filteredStudies.length > 0 &&
        <div className="mt-28">
            <div className="text-center mb-24">
              <h3 className="font-headline text-6xl md:text-7xl text-brand-plum mb-10 font-bold">
                Project Highlights
              </h3>
              <p className="font-body text-2xl md:text-3xl text-text-secondary max-w-3xl mx-auto leading-relaxed">
                Behind every success is a story of transformation.
              </p>
            </div>

            {/* 3-column photo grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10 lg:gap-12">
              {filteredStudies.slice(0, 6).map((study, index) =>
            <GridHighlightCard key={`grid-${study.id}`} study={study} index={index} categories={categories} />
            )}
            </div>
          </div>
        }

        {filteredStudies.length === 0 &&
        <div className="text-center py-32">
            <p className="font-body text-3xl text-text-secondary">No projects found in this category.</p>
          </div>
        }
      </div>
    </section>);

}